import { ReactNode } from 'react';

/**
 * A row of items travelling steadily sideways, looping forever.
 *
 * The loop is pure CSS on a duplicated track: the track holds the children
 * twice and slides exactly half its own width, so it returns to its starting
 * pixel with no seam and nothing to measure at runtime. That matters here
 * because this page is prerendered to static HTML — a JS-driven ticker would
 * leave a dead strip in the snapshot until hydration.
 *
 * The second copy is `aria-hidden`: it is a visual repeat, and a screen reader
 * should hear the list once.
 */

interface MarqueeProps {
  /** Items to repeat. Keys come from the caller's own data. */
  children: ReactNode;
  /** Seconds for one full pass. Longer content wants a longer duration to hold
      a steady apparent speed — roughly trackWidth / 50 px per second. */
  durationSeconds: number;
  /** Travel right instead of left. Facing rows use opposite directions. */
  reverse?: boolean;
  /** Gap between items, as a Tailwind gap class. */
  gapClassName?: string;
  className?: string;
}

export function Marquee({
  children,
  durationSeconds,
  reverse = false,
  gapClassName = 'gap-4',
  className = '',
}: MarqueeProps) {
  return (
    <div className={`fias-marquee ${reverse ? 'fias-marquee-reverse' : ''} ${className}`}>
      <div
        className="fias-marquee-track"
        style={{ ['--fias-marquee-duration' as string]: `${durationSeconds}s` }}
      >
        <div className={`flex shrink-0 ${gapClassName} pr-4`}>{children}</div>
        <div className={`flex shrink-0 ${gapClassName} pr-4`} aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
